import { App } from "antd";
import $ from "jquery";
import { useCallback, useEffect } from "react";
import { runFolderTreeDrop, useJqueryFolderTreeDroppable } from "@devowl-wp/react-folder-tree";
import { RatingPointer, isRatable } from "@devowl-wp/real-utils";
import { useAppTreeControllerRef } from "../../../contexts/appTreeControllerContext.js";
import { useAppTreeMountContext } from "../../../contexts/appTreeMountContext.js";
import { RML_ATTACHMENT_MOVE_FINISHED } from "../../../events/folderLifecycle.js";
import { __ } from "../../../utils/i18n.js";
import { request } from "../../../utils/request.js";
import { rmlOpts } from "../../../utils/rmlOpts.js";
function getBulkActionMessage(kind, phase, count) {
    const isOne = count === 1;
    if (kind === "move") {
        if (phase === "LoadingText") {
            return isOne ? __("Moving one file...") : __("Moving %d files...", count);
        }
        return isOne ? __("Successfully moved one file") : __("Successfully moved %d files", count);
    }
    if (phase === "LoadingText") {
        return isOne ? __("Copying one file...") : __("Copying %d files...", count);
    }
    return isOne ? __("Successfully copied one file") : __("Successfully copied %d files", count);
}
function iterateDraggedItem(ui, container, listMode, gridMode) {
    const { attachmentsBrowser } = container;
    if (attachmentsBrowser) {
        const browser = attachmentsBrowser;
        const selection = browser.options.selection.models;
        if (selection.length) {
            selection.forEach((model)=>{
                gridMode?.(model.attributes, attachmentsBrowser);
            });
            return selection.length;
        }
        const id = ui.data("id");
        gridMode?.(browser.collection.models.filter((model)=>model.id === id)[0], attachmentsBrowser);
        return 1;
    }
    const trs = $("#the-list > tr > .check-column > input:checked");
    if (trs.length) {
        trs.each(function() {
            listMode?.($(this).parents("tr"));
        });
    } else {
        listMode?.(ui);
    }
    return trs.length || 1;
}
function useAttachmentDroppable() {
    const { message } = App.useApp();
    const { id: treeId } = useAppTreeMountContext();
    const controllerRef = useAppTreeControllerRef();
    const onDrop = useCallback(async (event, ui)=>{
        const element = controllerRef.current;
        const activeId = element.getSelectedId();
        const elements = [];
        const fnFade = (percent)=>elements.forEach((obj)=>obj.fadeTo(250, percent));
        const { store } = element.props;
        let hide;
        let dropCounts;
        await runFolderTreeDrop({
            $,
            event,
            ui,
            collect: ()=>{
                const ids = [];
                iterateDraggedItem(ui.draggable, element, (tr)=>{
                    ids.push(+tr.find('input[type="checkbox"]').attr("value"));
                    elements.push(tr);
                }, (attributes, browser)=>{
                    const { id: attachmentId } = attributes;
                    ids.push(attachmentId);
                    elements.push(browser.$el.find(`li[data-id="${attachmentId}"]`));
                });
                return {
                    ids
                };
            },
            onBefore: ({ ids, isCopy, to })=>{
                element.setState({
                    isTreeLinkDisabled: true
                });
                fnFade(0.3);
                if (element.attachmentsBrowser) {
                    store.addFoldersNeedsRefresh(to);
                    to === +rmlOpts.others.rootId && store.addFoldersNeedsRefresh(activeId);
                }
                const bulkKind = isCopy ? "append" : "move";
                hide = message.loading(getBulkActionMessage(bulkKind, "LoadingText", ids.length));
            },
            execute: async ({ ids, isCopy, to })=>{
                const { counts, removedFolderIds } = await request({
                    location: {
                        path: "/attachments/bulk/move",
                        method: "PUT"
                    },
                    request: {
                        ids,
                        to,
                        isCopy
                    }
                });
                removedFolderIds?.forEach((folderId)=>{
                    const obj = store.getTreeItemById(+folderId);
                    obj?.visible(false);
                });
                dropCounts = counts;
            },
            onSuccess: ({ ids, isCopy, to })=>{
                document.dispatchEvent(new CustomEvent(RML_ATTACHMENT_MOVE_FINISHED, {
                    detail: {
                        ids,
                        to: +to,
                        isCopy
                    },
                    bubbles: true
                }));
                isRatable(rmlOpts.slug) && new RatingPointer(rmlOpts.slug, $(event.target));
                message.success(getBulkActionMessage(isCopy ? "append" : "move", "Success", ids.length));
                element.fetchCounts(dropCounts);
                elements.forEach((obj)=>{
                    const attachmentPreview = obj.children(".attachment-preview");
                    if (obj.hasClass("selected") && attachmentPreview.length) {
                        attachmentPreview.click();
                    }
                });
                const fadeBack = isCopy || !isCopy && activeId === to || activeId === "all";
                if (fadeBack) {
                    fnFade(1);
                } else {
                    elements.forEach((obj)=>obj.remove());
                }
                if (activeId === "all" && isCopy || isCopy && activeId === to) {
                    element.handleReload();
                }
                if (!element.attachmentsBrowser && !$(".wp-list-table tbody tr").length) {
                    $(".wp-list-table tbody").html(`<tr class="no-items"><td class="colspanchange" colspan="6">${__("No entries found")}</td></tr></tbody>`);
                }
            },
            onError: (error)=>{
                message.error(error.responseJSON.message);
                fnFade(1);
            },
            onFinally: ()=>{
                hide?.();
                element.setState({
                    isTreeLinkDisabled: false
                });
            }
        });
    }, [
        controllerRef,
        message
    ]);
    const { makeDroppable, refreshDroppable } = useJqueryFolderTreeDroppable({
        $,
        root: `#${treeId}`,
        nodeSelector: ".aiot-node.aiot-droppable[data-id!='all']",
        onDrop
    });
    useEffect(()=>{
        controllerRef.current.refreshDroppable = refreshDroppable;
        return ()=>{
            delete controllerRef.current.refreshDroppable;
        };
    }, [
        controllerRef,
        refreshDroppable
    ]);
    useEffect(()=>{
        if (controllerRef.current?.ref?.container) {
            makeDroppable();
        }
    }, [
        controllerRef,
        makeDroppable,
        treeId
    ]);
    return {
        makeDroppable,
        refreshDroppable
    };
}
export { useAttachmentDroppable };
